import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit, Output, TemplateRef, ViewChild, EventEmitter, ViewEncapsulation, Input, PipeTransform, Pipe, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DownloadComponent } from 'src/app/shared/components/download/download.component';
import { AreaTypeService } from 'src/app/shared/services/area-type.service';
import { DebugService } from 'src/app/shared/services/debug.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { DownloadService } from 'src/app/shared/services/download.service';
import { FilterService } from 'src/app/shared/services/filter.service';
import { PopupService } from 'src/app/shared/services/popup.service';
import { Store } from '@ngrx/store';
import { IMarisData } from 'src/app/shared/interfaces/maris-data.interface';
import { AnalysisOptionsComponent } from './analysis-options/analysis-options.component';
import { CommonSelectors } from 'src/app/store';

type FilterOptions = {
    analysisOption?: number;
    skip?: {
        shapeType?: boolean;
        analysisOption?: boolean;
        nuclide?: boolean;
    };
    keepNuclide?: boolean;
};

type FilterActions = 'reset' | 'sampleTypeChanged' | 'analysisOptionChanged' | 'applySliderValue' | 'resetSamplingDepth';

@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class FilterComponent implements OnInit, AfterViewInit {
    @ViewChild('dataSelectorTemplate') dataSelectorTemplate: TemplateRef<any>;
    @ViewChild('sliderTemplate') sliderTemplate: TemplateRef<any>;
    @ViewChild('filterValuesListTemplate') filterValuesListTemplate: TemplateRef<any>;
    @ViewChild(AnalysisOptionsComponent) analysisOptionsComponent: AnalysisOptionsComponent;

    @Output() onShapeTypeChanged = new EventEmitter<number>();
    @Output() filterChanged = new EventEmitter<number>();
    @Output() summaryStatisticsChange = new EventEmitter<any>();

    @Input() marisData: IMarisData = null;
    @Input() sampleCount = 0;
    @Input() measureCount = 0;
    @Input() measurementData = [];

    nuclides$ = this.store.select(CommonSelectors.selectNuclides);
    isMobileOrTabled = window.innerWidth < 768;

    private _filterActionsHistory: FilterActions[] = [];

    get isSamplingDepthDirty() {
        const resetSamplingDepthIndex = this._filterActionsHistory.lastIndexOf('resetSamplingDepth');
        const applySliderValueIndex = this._filterActionsHistory.lastIndexOf('applySliderValue');

        return applySliderValueIndex > resetSamplingDepthIndex;
    }

    get isSampleAnalysis() {
        return this.analysisOptionsComponent?.analysisOptionId === 1;
    }

    get isMeasurementAnalysis() {
        return this.analysisOptionsComponent?.analysisOptionId === 2;
    }

    get nuclideOptions() {
        return this.filterData?.defaultFilterValues['ss' + this.selectedSampleTypeId + this.selectedShapeTypeId].nuclides.data || [];
    }

    get selectedNuclideOption() {
        return this.nuclideOptions?.find((x) => x.id === this.singleSelectedNuclideId);
    }

    get sampleTypeValue() {
        return this.filterData.filterValues.sampleType.find((x) => x.id === this.selectedSampleTypeId)?.value;
    }

    get isBiotaSampleType() {
        return this.selectedSampleTypeId === 2;
    }

    get isBiotaAndMeasurementAnalysis() {
        return this.isBiotaSampleType && this.isMeasurementAnalysis;
    }

    get isLastFilterActionAnalysisOptionChanged() {
        return this._filterActionsHistory.length && this._filterActionsHistory[this._filterActionsHistory.length - 1] === 'analysisOptionChanged';
    }

    get isLastFilterActionSampleTypeChanged() {
        return this._filterActionsHistory.length && this._filterActionsHistory[this._filterActionsHistory.length - 1] === 'sampleTypeChanged';
    }

    get nuclideSingleSelectionList() {
        const pipe = new RemoveDuplicatesByIdPipe();
        // find selected nuclides from oldSelectedValueSet by id
        const selectedNuclides = this.memorizedSelectedNuclidesInSampleMode;
        const nuclidesToSelect = this.memorizedSelectedNuclidesInSampleMode.length
            ? this.newSelectedValueSet.nuclides.data.filter((x) => selectedNuclides.includes(x.id) && !x.isDisabled)
            : this.newSelectedValueSet.nuclides.data.filter((x) => selectedNuclides.includes(x.id) && !x.isDisabled);

        if (nuclidesToSelect.length)
            return [
                { value: 'SELECTED NUCLIDES', isDisabled: true },
                ...pipe.transform(nuclidesToSelect),
                { value: '------------------------------------', isDisabled: true },
                { value: 'ALL NUCLIDES', isDisabled: true },
                ...pipe.transform(this.newSelectedValueSet.nuclides.data),
            ];
        else return [...pipe.transform(this.newSelectedValueSet.nuclides.data)];
    }

    paramTid;
    paramRid;

    expanded = true;

    filterData = null;
    filters = null;
    filterLoaded: boolean;
    lastClickedFilterItem: any;

    //--------------- Filter Control Selected Values ----------------
    private selectedAnalysisOptionId: number = 1;

    selectedSampleTypeId: number = 1;
    selectedShapeTypeId: number = 1;
    selectedSummaryStatisticsId: string = 'uniqueSampleCount';
    singleSelectedNuclideId: number = null;
    selectedNuclide = null;
    memorizedSelectedNuclidesInSampleMode: number[] = [];
    //---------------------------------------------------------------
    showModal = false;
    modalBoxFilterValues = null;

    showDialog = false;
    agree = false;

    seaAreaFilter = { id: 'seaAreas', value: 'Area' };
    seaAreas = [];
    subAreas = [];
    modalTitle = '';
    FilterValuedata: any;

    openedFilterControl = null;
    openedFilterValueSet = null;
    mainFilterControl = null;
    defaultValueSet = null;
    oldSelectedValueSet = null;
    newSelectedValueSet = null;
    userSelectedValueSet = null;
    selectionPristine = true;
    defaultFilterResult = null;

    filterValues: any;

    activeComponent: any;

    defaultValues = { min: -1, max: 9999 };
    relevantValues = { min: -1, max: 9999 };
    oldSelectionValues = { min: null, max: null };
    newSelectionValues = { min: null, max: null };
    userSelectedSliderValue = false;

    helpContent = {
        sampleTypes: 'Data from a single sample type - seawater, biota, sediment or suspended matter can be accessed at one time. Choose the sample type of interest using the tab on the left.',
        shapeTypes:
            "Refine data by marine geographic areas using the 'area type' and 'areas' filters. Currently, two marine region types are supported: IHO Sea Areas and FAO Major Fishing Areas (see 'Help' for more details).",
        seaAreas: 'For each area type, marine areas and nested sub-areas are presented in the popup window on the globe/ map. To filter the data, deselect areas that are not of interest.',
        nuclides: 'Filter by radionuclide(s) (defined by mass number and symbol of the element, e.g. 137Cs).',
        samplingYears: 'Filter by the year(s) in which the samples were collected.',
        dataSets: "Filter by source datasets. Individual datasets can be explored via the 'Datasets' page.",
        samplingDepth:
            "Filter by a range of sampling depths (m). A value of '-1' indicates that sample depth information is not available. The min and max values above the depth slider are user selected values. The min and max values below the depth slider indicate relevant sample depth range based on the other filter selections. The min and max values on the left and right side of the depth slider indicate the default sample depth range.",
        bioGroups:
            'Filter by group(s) of related species (e.g. crustaceans, fish etc.) and by taxonomic identifier(s).The taxonomic identifier list is comprised of values representing the most detailed taxonomic information available for each sample and contains information from different taxon ranks (e.g. species, genus etc.).',
        bodyParts: 'Filter by the part(s) of the organism(s) analysed.',
        sedimentTypes: 'Filter by sediment type(s). The list of values is based on the Udden-Wentworth scale.',
        export: 'Drag export data box if it covers the filter',
    };

    cacheKeys = {
        cacheKey11: 'marisSeaWaterIHO',
        cacheKey21: 'marisBiotaIHO',
        cacheKey31: 'marisSedimentIHO',
        cacheKey41: 'marisSuspendedMatterIHO',
        cacheKey12: 'marisSeaWaterFAO',
        cacheKey22: 'marisBiotaFAO',
        cacheKey32: 'marisSedimentFAO',
        cacheKey42: 'marisSuspendedMatterFAO',
    };

    resetSlider = false;

    constructor(
        private cdRef: ChangeDetectorRef,
        private debugService: DebugService,
        private filterService: FilterService,
        private downloadService: DownloadService,
        private dialogService: DialogService,
        private route: ActivatedRoute,
        private location: Location,
        private popupService: PopupService,
        private areaTypeService: AreaTypeService,
        private store: Store
    ) {
        this.route.params.subscribe((params) => {
            this.paramTid = params['tid'];
            this.paramRid = params['rid'];
        });
    }

    ngOnInit(): void {
        this.dialogService.activeComponent().subscribe((activeComponent) => {
            if (!activeComponent) {
                this.closeDialog();
            }
            this.activeComponent = activeComponent;
        });
    }

    resetFilterAndAreaTypeService() {
        this.resetFilter(true);
        this.areaTypeService.setBoundaryStatus({
            showBoundaryStatus: false,
            shapeTypeId: this.selectedShapeTypeId,
        });
    }

    anyNotDisabled(items) {
        return items.some((x) => !x.isDisabled);
    }

    ngAfterViewInit(): void {
        this.initFilterData();
        this.initValueSets();
        this.resetFilter();

        if (this.paramTid) {
            if (!this.paramRid) {
                this.paramRid = null;
                this.applyFilter('reset');
            } else {
                this.applyFilter('ref');
            }
        } else {
            this.applyFilter('reset');
        }

        this.resetSamplingDepth();
        this.cdRef.detectChanges();

        this.filterLoaded = true;
    }

    @HostListener('click', ['$event'])
    public handleClick(event) {
        if (event.target.id == 'container') {
            console.log('clicked on dialog');
            this.openDialog();
        }
    }

    @HostListener('document:keydown', ['$event'])
    onKeyDownHandler(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.showDialog = false;
        }
    }

    constructSeaArea(shapeTypeId: number) {
        this.seaAreas = this.filterData.filterValues.seaAreas['sh' + shapeTypeId];
        this.subAreas = this.seaAreas.map((data) => {
            return data.subarea ? JSON.parse('[' + data.subarea.replace('&amp;#x0D;', '').replace(/\n/g, '') + ']') : [data];
        });

        this.subAreas = this.subAreas.reduce((acc, val) => acc.concat(val), []);
    }

    switchSampleType(sampleType: any, isClicked?: boolean) {
        this.selectedSampleTypeId = sampleType.id;
        this.selectedShapeTypeId = 1;
        if (isClicked) {
            this.location.replaceState('/explore');
        }
    }

    onSampleTypeChange(sampleType: any) {
        this.pushFilterActionHistory('sampleTypeChanged');

        let resetFilterOptions: FilterOptions = null;
        this.selectedSampleTypeId = sampleType.id;

        const selectedNuclidesCount = this.newSelectedValueSet['nuclides'].data.filter((x) => x.isSelected).map((x) => x.id).length;
        const totalNuclidesCount = this.newSelectedValueSet['nuclides'].data.length;

        const keepNuclide = this.selectedAnalysisOptionId === 1 ? selectedNuclidesCount !== totalNuclidesCount : this.singleSelectedNuclideId !== null;

        resetFilterOptions = {
            analysisOption: this.selectedAnalysisOptionId,
            skip: { analysisOption: false },
            keepNuclide: false, // keepNuclide,
        };

        if (sampleType.id == 4 && this.selectedAnalysisOptionId === 2) {
            this.selectedAnalysisOptionId = 1;
            resetFilterOptions.keepNuclide = false;
        }

        this.resetFilter(true, resetFilterOptions);

        this.areaTypeService.setBoundaryStatus({
            showBoundaryStatus: false,
            shapeTypeId: this.selectedShapeTypeId,
        });
    }

    onShapeTypeChange(shapeTypeId: number) {
        this.onShapeTypeChanged.emit(shapeTypeId);
        this.resetFilter(true, { skip: { shapeType: true } });
        this.areaTypeService.setBoundaryStatus({
            showBoundaryStatus: false,
            shapeTypeId: shapeTypeId,
        });
    }

    isActive(valueType: string, id: number) {
        switch (valueType) {
            case 'sampleType':
                return this.selectedSampleTypeId == id;
            case 'shapeType':
                return this.selectedShapeTypeId == id;
        }
    }

    getSampleTypeIcon(sampleTypeId: number) {
        let extension = this.selectedSampleTypeId == sampleTypeId ? '.svg' : '_white.svg';
        return '/assets/icons/svg/st' + sampleTypeId + '_icon' + extension;
    }

    toggleFilterPanel() {
        this.expanded = !this.expanded;
    }

    showFilter(filterId: string) {
        let occuranceArray = this.filterData.configuration['st' + this.selectedSampleTypeId];

        if (this.selectedAnalysisOptionId === 2) {
            occuranceArray = occuranceArray.filter((e) => e !== 'nuclides');
        }

        return occuranceArray.includes(filterId);
    }

    constructFilterValues(filter: any) {
        let newSelectedValueSet = JSON.parse(JSON.stringify(this.newSelectedValueSet[filter.id].data));

        switch (filter.id) {
            case 'seaAreas':
                this.modalBoxFilterValues = {
                    id: filter.id,
                    tp: this.filterValuesListTemplate,
                    isAllSelected: true,
                    isAllCollapsed: true,
                    parentChild: true,
                    flexWidth: '250px',
                    values: this.selectionControl(newSelectedValueSet, filter.id),
                    defaultValues: this.selectionControl(newSelectedValueSet, filter.id),
                };
                break;
            case 'bioGroups':
                this.modalBoxFilterValues = {
                    id: filter.id,
                    tp: this.filterValuesListTemplate,
                    isAllSelected: true,
                    isAllCollapsed: true,
                    parentChild: true,
                    flexWidth: '250px',
                    values: this.selectionControl(newSelectedValueSet, filter.id),
                    defaultValues: this.selectionControl(newSelectedValueSet, filter.id),
                };
                break;
            case 'bodyParts':
                this.modalBoxFilterValues = {
                    id: filter.id,
                    tp: this.filterValuesListTemplate,
                    isAllSelected: true,
                    isAllCollapsed: true,
                    parentChild: false,
                    flexWidth: 25,
                    values: this.selectionControl(newSelectedValueSet, filter.id),
                    defaultValues: this.selectionControl(newSelectedValueSet, filter.id),
                };
                break;
            case 'dataSets':
                this.modalBoxFilterValues = {
                    id: filter.id,
                    tp: this.filterValuesListTemplate,
                    isAllSelected: true,
                    isAllCollapsed: true,
                    parentChild: false,
                    flexWidth: 25,
                    values: this.selectionControl(newSelectedValueSet, filter.id),
                    defaultValues: this.selectionControl(newSelectedValueSet, filter.id),
                };
                break;
            case 'nuclides':
                this.modalBoxFilterValues = {
                    id: filter.id,
                    tp: this.filterValuesListTemplate,
                    isAllSelected: true,
                    isAllCollapsed: true,
                    parentChild: false,
                    flexWidth: 16,
                    values: this.selectionControl(newSelectedValueSet, filter.id),
                    defaultValues: this.selectionControl(newSelectedValueSet, filter.id),
                };
            case 'samplingDepth':
                break;
            default:
                this.modalBoxFilterValues = {
                    id: filter.id,
                    tp: this.filterValuesListTemplate,
                    isAllSelected: true,
                    isAllCollapsed: true,
                    parentChild: false,
                    flexWidth: 16,
                    values: this.selectionControl(newSelectedValueSet, filter.id),
                    defaultValues: this.selectionControl(newSelectedValueSet, filter.id),
                };
                break;
        }
        this.unhideFilterValues();
    }

    applySeaAreaFilterValue(areaId: string) {
        const currentFilterData = JSON.parse(JSON.stringify(this.newSelectedValueSet['seaAreas'].data));
        // set selected area or subarea and select all children and unselect all other areas and subareas
        currentFilterData.flat().forEach((area) => {
            if (area.id == areaId) {
                area.isSelected = true;
                area.childList?.forEach((subarea) => {
                    subarea.isSelected = true;
                });
            } else {
                area.isSelected = false;
                area.childList.forEach((subarea) => {
                    if (subarea.id == areaId) {
                        subarea.isSelected = true;
                    } else {
                        subarea.isSelected = false;
                    }
                });
            }
        });

        const defaultFilterDataValue = this.defaultValueSet['seaAreas'].data;
        this.applyFilter('filter', false, currentFilterData, defaultFilterDataValue);
    }

    applyNuclideFilterValue(nuclideId: string) {
        const nuclides = nuclideId.split(',');
        const currentFilterData = JSON.parse(JSON.stringify(this.newSelectedValueSet['nuclides'].data));
        currentFilterData.forEach((nuclide) => {
            if (nuclides.includes(nuclide.id.toString())) {
                nuclide.isSelected = true;
            } else {
                nuclide.isSelected = false;
            }
        });

        const defaultFilterDataValue = this.defaultValueSet['nuclides'].data;
        this.applyFilter('filter', false, currentFilterData, defaultFilterDataValue);
    }

    selectionControl(newSelectedValueSet: any, filterId: string) {
        let result = {
            multiColumns: false,
            data: [],
        };

        let values;

        if (this.mainFilterControl == null && this.selectionPristine) {
            values = JSON.parse(JSON.stringify(newSelectedValueSet));
        } else {
            values = JSON.parse(JSON.stringify(this.newSelectedValueSet[filterId].data));
        }

        // if it's measurements mode and sample type is biota, then filterout biota by id. Available ids = (2,4,6,11,12,13,14)
        // if (this.isBiotaAndMeasurementAnalysis) {
        //     values = values.filter((item) => {
        //         return [2, 4, 6, 11, 12, 13, 14].includes(item.id);
        //     });
        // }

        if (filterId == 'seaAreas' || filterId == 'bioGroups') {
            result.multiColumns = true;
            let columns = 3;
            let rows = Math.round(values.length / columns);

            for (let i = 0; i < columns; i++) {
                var arr = [];
                for (let j = 0; j <= rows; j++) {
                    var value = values[3 * j + i];
                    if (value) {
                        arr.push(value);
                    }
                }
                result.data.push(arr);
            }
        } else {
            result.data = values;
        }

        return result;
    }

    showPopup(filter: any) {
        this.hidePopup();
        this.modalTitle = 'Refine ' + filter.value;
        this.constructFilterValues(filter);
        this.showModal = true;
        this.openedFilterControl = filter;
        this.debugService.output('Filter', 'showPopup', this.openedFilterControl);
    }

    hidePopup() {
        this.showModal = false;
        this.openedFilterControl = null;
        this.modalBoxFilterValues = null;
    }

    q(searchValue: string) {
        if (searchValue !== '') {
            this.unhideFilterValues();

            this.modalBoxFilterValues.values.data.forEach((item) => {
                if (Array.isArray(item)) {
                    item.forEach((data) => {
                        let count = 0;
                        data.childList.forEach((element) => {
                            if (element.value.toString().toUpperCase().indexOf(searchValue.toUpperCase()) == -1) {
                                element.isHidden = true;
                            } else {
                                count += 1;
                            }
                        });

                        if (count == 0) {
                            data.isHidden = true;
                        }
                    });
                } else {
                    let count = 0;
                    if (item.childList) {
                        item.childList.forEach((element) => {
                            if (element.value.toString().toUpperCase().indexOf(searchValue.toUpperCase()) == -1) {
                                element.isHidden = true;
                            } else {
                                count += 1;
                            }
                        });

                        if (count == 0) {
                            item.isHidden = true;
                        }
                    } else {
                        if (item.value.toString().toUpperCase().indexOf(searchValue.toUpperCase()) == -1) {
                            item.isHidden = true;
                        }
                    }
                }
            });
        } else {
            this.unhideFilterValues();
        }
    }

    unhideFilterValues() {
        this.modalBoxFilterValues.values.data.forEach((item) => {
            if (Array.isArray(item)) {
                item.forEach((data) => {
                    data.isHidden = false;
                    if (data.childList) {
                        data.childList.forEach((element) => {
                            element.isHidden = false;
                        });
                    }
                });
            } else {
                item.isHidden = false;
                if (item.childList) {
                    item.childList.forEach((element) => {
                        element.isHidden = false;
                    });
                }
            }
        });
    }

    getCount(filterId: string) {
        if (
            this.defaultValueSet[filterId].count == this.newSelectedValueSet[filterId].count ||
            this.newSelectedValueSet[filterId].data.length == this.newSelectedValueSet[filterId].data.filter((x) => x.isSelected).length
        ) {
            return 'All';
        } else {
            return this.newSelectedValueSet[filterId].count + ' selected';
        }
    }

    getSliderData(data: string) {
        switch (data) {
            case 'defaultValues':
                return this.defaultValues;

            case 'relevantValues':
                return this.relevantValues;

            case 'oldSelectionValues':
                return this.oldSelectionValues;

            case 'newSelectionValues':
                return this.newSelectionValues;
        }
    }

    resetFilter(isClicked?: boolean, filterOptions: FilterOptions = null) {
        this.resetDefaultValueSet();
        this.resetFilterControls(filterOptions);
        this.resetPopup();
        this.selectionPristine = true;
        this.mainFilterControl = null;
        if (isClicked) {
            this.oldSelectedValueSet = JSON.parse(JSON.stringify(this.newSelectedValueSet));
        }

        if (isClicked) {
            this.paramRid = null;
            this.paramTid = null;
        }

        if (!this.paramRid && !this.paramTid) {
            this.applyFilter('reset', null, null, null, filterOptions, isClicked);
        }
    }

    //Click event on parent checkbox
    parentCheck(parentObj) {
        parentObj.isIndeterminate = false;
        if (parentObj.childList) {
            for (var i = 0; i < parentObj.childList.length; i++) {
                if (!parentObj.childList[i].isDisabled) {
                    parentObj.childList[i].isSelected = parentObj.isSelected;
                }
            }
        }
    }

    //Click event on parent checkbox
    parentShiftHoldCheck(parentObj, items = null, $event = null) {
        parentObj.isSelected = !parentObj.isSelected;
        /*
            We want to use shift key to select multiple items.
            If shift key is pressed, we want to select all items between the last clicked item and current item.
            If shift key is not pressed, we want to select only current item.
        */
        if ($event && $event.shiftKey && this.lastClickedFilterItem) {
            const lastClickedItemIndex = items.findIndex((x) => x.id === this.lastClickedFilterItem.id);
            const currentItemIndex = items.findIndex((x) => x.id === parentObj.id);
            const startIndex = Math.min(lastClickedItemIndex, currentItemIndex);
            const endIndex = Math.max(lastClickedItemIndex, currentItemIndex);

            for (let i = startIndex; i <= endIndex; i++) {
                if (!items[i].isDisabled) {
                    items[i].isSelected = parentObj.isSelected;
                }
            }
        } else {
            parentObj.isSelected = !parentObj.isSelected;
        }

        this.lastClickedFilterItem = parentObj;

        parentObj.isIndeterminate = false;
        if (parentObj.childList) {
            for (var i = 0; i < parentObj.childList.length; i++) {
                if (!parentObj.childList[i].isDisabled) {
                    parentObj.childList[i].isSelected = parentObj.isSelected;
                }
            }
        }
    }

    //Click event on child checkbox
    childCheck(parentObj, childObj) {
        // parentObj.isSelected = childObj.every(function (itemChild: any) {
        //   return itemChild.isSelected == true;
        // });
        let selectCount = 0;
        childObj.forEach((itemChild) => {
            if (itemChild.isSelected == true) {
                selectCount += 1;
            }
        });
        if (selectCount == childObj.length) {
            parentObj.isSelected = true;
        }
        if (selectCount == 0) {
            parentObj.isSelected = false;
        }

        if (selectCount > 0 && selectCount < childObj.length) {
            parentObj.isSelected = false;
            parentObj.isIndeterminate = true;
        } else {
            parentObj.isIndeterminate = false;
        }
    }

    //Click event on master select
    selectUnselectAll(obj, select) {
        if (select) {
            obj.isAllSelected = true;
        } else {
            obj.isAllSelected = false;
        }

        if (Array.isArray(obj.values.data[0])) {
            obj.values.data.forEach((item) => {
                for (var i = 0; i < item.length; i++) {
                    if (!item[i].isDisabled) {
                        item[i].isSelected = obj.isAllSelected;
                    }
                    if (obj.parentChild) {
                        for (var j = 0; j < item[i].childList.length; j++) {
                            if (!item[i].childList[j].isDisabled) {
                                item[i].childList[j].isSelected = obj.isAllSelected;
                            }
                        }
                    }
                }
            });
        } else {
            for (var i = 0; i < obj.values.data.length; i++) {
                if (!obj.values.data[i].isDisabled) {
                    obj.values.data[i].isSelected = obj.isAllSelected;
                }
                if (obj.parentChild) {
                    for (var j = 0; j < obj.values.data[i].childList.length; j++) {
                        if (!obj.values.data[i].childList[j].isDisabled) {
                            obj.values.data[i].childList[j].isSelected = obj.isAllSelected;
                        }
                    }
                }
            }
        }
    }

    //Expand/Collapse event on each parent
    expandCollapse(obj) {
        obj.isClosed = !obj.isClosed;
    }

    //Master expand/ collapse event
    expandAll(obj) {
        obj.values.data.forEach((item) => {
            for (var i = 0; i < item.length; i++) {
                item[i].isClosed = false;
            }
        });
        obj.isAllCollapsed = false;
    }

    collapseAll(obj) {
        obj.values.data.forEach((item) => {
            for (var i = 0; i < item.length; i++) {
                item[i].isClosed = true;
            }
        });
        obj.isAllCollapsed = true;
    }

    applyFilter(mode: string, isSliderChange?: boolean, newFilterDataValueSet: object = null, defaultFilterDataValues: any = null, filterOptions: FilterOptions = null, isClicked: boolean = false) {
        //mode: "reset" => reset to default, "ref" => dataset view, "filter" => user selection view
        if (mode == 'reset') {
            let selectedSampleType;

            if (this.paramTid) {
                this.filterData.filterValues.sampleType.forEach((item) => {
                    if (item.id == this.paramTid) {
                        selectedSampleType = item;
                    }
                });
                this.switchSampleType(selectedSampleType);
                this.resetFilter();
            }
            if (!filterOptions?.keepNuclide) {
                this.memorizedSelectedNuclidesInSampleMode = [];
            }
            const isSampleAnalysis = this.selectedAnalysisOptionId == 1;
            const summaryStatisticsMode = isSampleAnalysis ? 'uniqueSampleCount' : 'meanActivity';

            let filterValues = {
                sampleTypeId: this.selectedSampleTypeId,
                areaId: null,
                subAreaId: null,
                nuclideId: isSampleAnalysis ? this.getSelectedNuclideIds(this.newSelectedValueSet).join(',') : this.singleSelectedNuclideId.toString(),
                samplingYear: null,
                reference: null,
                samplingDepthFrom: null,
                samplingDepthTo: null,
                bioGroup: null,
                bodyPar: null,
                sedimentType: null,
                shapeTypeId: this.selectedShapeTypeId,
                reset: false,
                cacheKey: this.cacheKeys['cacheKey' + this.selectedSampleTypeId + this.selectedShapeTypeId],
                lastUpdate: this.marisData.lastUpdate.lastUpdate,
                search: false,
                summaryStatisticsMode: this.selectedSummaryStatisticsId,
            };

            if (filterOptions?.keepNuclide) {
                filterValues.reset = false;
                filterValues.cacheKey = null;
                filterValues.lastUpdate = null;
            }

            filterValues.summaryStatisticsMode = summaryStatisticsMode;

            this.filterValues = filterValues;

            if (isClicked) {
                this.filterService.getFilterValues(this.filterValues).subscribe((result) => {
                    this.userSelectedValueSet = null;
                    this.userSelectedSliderValue = false;
                    let resultObj = result.filterValue;

                    this.updateFilterValueSet(resultObj);
                    this.sendFilterValue();
                });
            } else {
                this.sendFilterValue();
            }
        }

        if (mode == 'ref') {
            let selectedSampleType;

            this.filterData.filterValues.sampleType.forEach((item) => {
                if (item.id == this.paramTid) {
                    selectedSampleType = item;
                }
            });
            this.switchSampleType(selectedSampleType);
            this.initValueSets();
            this.resetFilter();

            let filterValues = {
                sampleTypeId: parseInt(this.paramTid),
                areaId: null,
                subAreaId: null,
                nuclideId: null,
                samplingYear: null,
                reference: this.paramRid,
                samplingDepthFrom: null,
                samplingDepthTo: null,
                bioGroup: null,
                bodyPar: null,
                sedimentType: null,
                shapeTypeId: 1,
                reset: false,
                cacheKey: null,
                lastUpdate: null,
                search: true,
                summaryStatisticsMode: this.selectedSummaryStatisticsId,
            };

            this.filterValues = filterValues;

            if (this.paramRid) {
                this.oldSelectedValueSet['dataSets'].data.forEach((item) => {
                    if (item.id != parseInt(this.paramRid)) {
                        item.isSelected = false;
                    }
                });
                this.oldSelectedValueSet['dataSets'].count = 1;

                this.mainFilterControl = { id: 'dataSets', value: 'dataSets' };
                this.filterService.getFilterValues(this.filterValues).subscribe((result) => {
                    this.userSelectedValueSet = null;
                    //let resultObj = JSON.parse(JSON.parse(result[0].resultData).filtervalue);
                    let resultObj = result.filterValue;

                    this.updateFilterValueSet(resultObj);

                    this.memorizedSelectedNuclidesInSampleMode = this.getSelectedNuclideIds(this.newSelectedValueSet);

                    this.sendFilterValue();
                });
            } else {
                this.sendFilterValue();
            }
        }

        if (mode == 'filter') {
            //this.unhideFilterValues();
            this.selectionPristine = false;
            this.userSelectedValueSet = this.newSelectedValueSet;

            if (!isSliderChange) {
                let currentFilterData = this.modalBoxFilterValues?.values?.data || newFilterDataValueSet;
                var defaultValuesData = this.modalBoxFilterValues?.defaultValues?.data?.flat() || defaultFilterDataValues?.flat();

                if (Array.isArray(currentFilterData[0])) {
                    currentFilterData = currentFilterData.flat();
                }

                if (JSON.stringify(currentFilterData) == JSON.stringify(defaultValuesData)) {
                    this.hidePopup();
                    return;
                }

                this.userSelectedValueSet[this.openedFilterControl.id].data = currentFilterData;
                this.userSelectedValueSet[this.openedFilterControl.id].count = 0;

                if (this.openedFilterControl.id == 'nuclides') {
                    this.memorizedSelectedNuclidesInSampleMode = this.getSelectedNuclideIds(this.userSelectedValueSet);
                }

                currentFilterData.forEach((item) => {
                    if (item.childList) {
                        let count = item.childList.filter((el) => {
                            return el.isSelected && !el.isDisabled;
                        }).length;
                        this.userSelectedValueSet[this.openedFilterControl.id].count += count;
                    } else {
                        if (item.isSelected && !item.isDisabled) {
                            this.userSelectedValueSet[this.openedFilterControl.id].count += 1;
                        }
                    }
                });
            }

            this.mainFilterControl = this.openedFilterControl;
            this.oldSelectedValueSet = JSON.parse(JSON.stringify(this.userSelectedValueSet));

            this.hidePopup();

            this.filterValues = this.constructFilterValueMapping();

            this.filterService.getFilterValues(this.filterValues).subscribe((result) => {
                this.userSelectedValueSet = null;
                this.userSelectedSliderValue = false;
                //let resultObj = JSON.parse(JSON.parse(result[0].resultData).filtervalue);
                let resultObj = result.filterValue;

                const valueSet = this.updateFilterValueSet(resultObj);
                this.filterValues = this.constructFilterValueMapping(valueSet);

                this.sendFilterValue();
            });

            this.location.replaceState('/explore');
        }
    }

    applySliderValue(event) {
        this.pushFilterActionHistory('applySliderValue');
        this.userSelectedSliderValue = true;
        var changed = false;

        if (event.changed == 'min') {
            this.oldSelectionValues.min = +event.min;
        } else {
            this.oldSelectionValues.max = +event.max;
        }

        if (this.newSelectionValues.min != +event.min) {
            changed = true;
            this.newSelectionValues.min = +event.min;
        }

        if (this.newSelectionValues.max != +event.max) {
            changed = true;
            this.newSelectionValues.max = +event.max;
        }

        if (changed == true) {
            this.openedFilterControl = {
                id: 'samplingDepth',
                value: 'Sampling Depth (m)',
            };
            this.newSelectedValueSet.samplingDepth.min = this.newSelectionValues.min;
            this.newSelectedValueSet.samplingDepth.max = this.newSelectionValues.max;
            this.applyFilter('filter', true);
        }
    }

    constructFilterValueMapping(valueSet = null) {
        valueSet = valueSet || this.userSelectedValueSet || {};
        let result = {};
        let keys = Object.keys(valueSet);

        const samplingDepthIndex = keys.indexOf('samplingDepth');
        if (samplingDepthIndex > -1) {
            keys.splice(samplingDepthIndex, 1);
        }

        const sampleTypeIndex = keys.indexOf('sampleType');
        if (sampleTypeIndex > -1) {
            keys.splice(sampleTypeIndex, 1);
        }

        for (const key of keys) {
            let data = valueSet[key].data;
            let selected = [];
            data.forEach((item) => {
                if (item.childList) {
                    item.childList.forEach((child) => {
                        if (child.isSelected == true) {
                            selected.push(child);
                        }
                    });
                } else {
                    if (item.isSelected == true) {
                        selected.push(item);
                    }
                }
            });

            let ssValue = JSON.parse(this.marisData.masterData['ss' + this.selectedSampleTypeId + this.selectedShapeTypeId][0].filtervalue)[key];

            if (selected.map((x) => x.id).length == ssValue.length) {
                result[key] = null;
            } else {
                result[key] = selected.map((x) => x.id).join();
            }
        }

        let filterMapping = {
            sampleTypeId: this.selectedSampleTypeId,
            areaId: result['seaAreas'],
            subAreaId: result['seaAreas'],
            nuclideId: this.getNuclideIds(result['nuclides']),
            samplingYear: result['samplingYears'],
            reference: result['dataSets'],
            samplingDepthFrom: this.userSelectedSliderValue ? parseInt(this.newSelectionValues.min) : -1,
            samplingDepthTo: this.userSelectedSliderValue ? parseInt(this.newSelectionValues.max) : 9999,
            bioGroup: result.hasOwnProperty('bioGroups') ? result['bioGroups'] : null,
            bodyPar: result.hasOwnProperty('bodyParts') ? result['bodyParts'] : null,
            sedimentType: result.hasOwnProperty('sedimentTypes') ? result['sedimentType'] : null,
            shapeTypeId: this.selectedShapeTypeId,
            reset: false,
            cacheKey: null,
            lastUpdate: null,
            search: true,
            summaryStatisticsMode: this.selectedSummaryStatisticsId,
        };

        return filterMapping;
    }

    updateFilterValueSet(result: any) {
        let newFilterValueSet = {};
        let keys = Object.keys(this.oldSelectedValueSet);

        const samplingDepthIndex = keys.indexOf('samplingDepth');
        if (samplingDepthIndex > -1) {
            keys.splice(samplingDepthIndex, 1);
        }

        const sampleTypeIndex = keys.indexOf('sampleType');
        if (sampleTypeIndex > -1) {
            keys.splice(sampleTypeIndex, 1);
        }

        for (const key of keys) {
            let newData = [];
            if (key != this.mainFilterControl?.id) {
                let idList = result[key];
                if (idList && key == 'seaAreas') {
                    idList = idList.map((val) => {
                        return val.trim();
                    });
                }
                let data = this.oldSelectedValueSet[key].data;
                let count = 0;

                data.forEach((item) => {
                    if (item.childList) {
                        //TODO: check indeterminate state
                        let subcount = 0;
                        let subcount_unchecked = 0;
                        item.isDisabled = true;
                        item.childList.forEach((child) => {
                            if (idList && !idList.includes(child.id)) {
                                child.isDisabled = true;
                            }

                            if (idList && idList.includes(child.id) && child.isSelected) {
                                child.isDisabled = false;
                                subcount += 1;
                            }

                            if (idList && idList.includes(child.id) && !child.isSelected) {
                                child.isDisabled = false;
                                subcount_unchecked += 1;
                            }

                            if (!idList) {
                                child.isDisabled = true;
                            }
                        });
                        if (subcount > 0 || subcount_unchecked > 0) {
                            item.isDisabled = false;
                        }
                        count += subcount;
                    } else {
                        if (idList && !idList.includes(item.id)) {
                            item.isDisabled = true;
                        }

                        if (idList && idList.includes(item.id) && item.isSelected) {
                            item.isDisabled = false;
                            count += 1;
                        }

                        if (idList && idList.includes(item.id) && !item.isSelected) {
                            item.isDisabled = false;
                        }

                        if (!idList) {
                            item.isDisabled = true;
                        }
                    }
                    if (!newData.find((x) => x.id == item.id)) {
                        newData.push(item);
                    }
                });

                if (key == 'seaAreas' || key == 'bioGroups') {
                    newData.sort(this.dynamicSort('value'));
                }
                newFilterValueSet[key] = { count: count, data: newData };
            } else {
                if (key == 'seaAreas' || key == 'bioGroups') {
                    this.oldSelectedValueSet[key].data.sort(this.dynamicSort('value'));
                }
                newFilterValueSet[key] = this.oldSelectedValueSet[key];
            }
        }

        this.checkRangeOverlap(result, newFilterValueSet);

        this.newSelectedValueSet = newFilterValueSet;
        //this.filterValues = newFilterValueSet;
        return newFilterValueSet;
    }

    checkRangeOverlap(result, newFilterValueSet) {
        var resultMin = parseInt(result.samplingDepthFrom);
        var resultMax = parseInt(result.samplingDepthTo);

        var resultRange = {
            min: isNaN(resultMin) ? 0 : resultMin,
            max: isNaN(resultMax) ? 0 : resultMax,
        };

        if (this.oldSelectionValues.min >= resultRange.min && this.oldSelectionValues.min <= resultRange.max) {
            this.newSelectionValues.min = this.oldSelectionValues.min;
        } else {
            this.newSelectionValues.min = resultRange.min;
        }

        if (this.oldSelectionValues.max >= resultRange.min && this.oldSelectionValues.max <= resultRange.max) {
            this.newSelectionValues.max = this.oldSelectionValues.max;
        } else {
            this.newSelectionValues.max = resultRange.max;
        }

        if (
            (this.oldSelectionValues.min > resultRange.min && this.oldSelectionValues.min > resultRange.max) ||
            (this.oldSelectionValues.max < resultRange.min && this.oldSelectionValues.max < resultRange.min)
        ) {
            this.newSelectionValues.min = -999;
            this.newSelectionValues.max = -999;
        }

        if (this.oldSelectionValues.min == null) {
            this.newSelectionValues.min = resultRange.min;
        }

        if (this.oldSelectionValues.max == null) {
            this.newSelectionValues.max = resultRange.max;
        }

        if (this.oldSelectionValues.max !== this.newSelectionValues.max) {
            this.oldSelectionValues.max = this.newSelectionValues.max;
        }

        this.relevantValues = {
            min: isNaN(resultMin) ? 0 : resultMin,
            max: isNaN(resultMax) ? 0 : resultMax,
        };

        newFilterValueSet['samplingDepth'] = {
            min: isNaN(resultMin) ? 0 : resultMin,
            max: isNaN(resultMax) ? 0 : resultMax,
        };
    }

    openDialog() {
        this.downloadService.setFilterValue(this.filterValues);
        this.showDialog = !this.showDialog;
        this.dialogService.displayComponent(DownloadComponent);
        this.dialogService.setDialogData({
            measurementMode: this.selectedAnalysisOptionId == 2 ? true : false,
        });
    }

    closeDialog() {
        this.showDialog = false;
    }

    getHelpContent(filter: string) {
        return this.helpContent[filter];
    }

    dynamicSort(property) {
        if (property[0] === '-') property = property.substr(1);

        return function (a, b) {
            if (typeof a[property] == 'number') {
                return a[property] - b[property];
            } else {
                return a[property].toLowerCase() < b[property].toLowerCase() ? -1 : a[property].toLowerCase() > b[property].toLowerCase() ? 1 : 0;
            }
        };
    }

    onAnalysisOptionChange(id: number) {
        this.pushFilterActionHistory('analysisOptionChanged');
        if (this.newSelectedValueSet) {
            this.resetNuclides(true);
            if (!this.isSamplingDepthDirty) {
                this.resetSamplingDepth();
            }
            // this.resetSamplingYears();
            // this.resetDataSets();
            this.applyFilterAnalysisOption(this.filterValues, id, this.newSelectedValueSet, true);
            this.singleSelectedNuclideId = parseInt(this.filterValues.nuclideId.split(',')[0]);
            this.selectedSummaryStatisticsId = this.filterValues.summaryStatisticsMode;
        }
        this.filterService.getFilterValues(this.filterValues).subscribe((result) => {
            this.userSelectedValueSet = null;
            let resultObj = result.filterValue;
            this.updateFilterValueSet(resultObj);
            this.sendFilterValue();
        });
    }

    onSummaryStatisticsChange(value: string) {
        this.filterValues.summaryStatisticsMode = this.selectedSummaryStatisticsId;

        this.sendFilterValue();

        this.filterService.getFilterValues(this.filterValues).subscribe((result) => {
            this.userSelectedValueSet = null;
            this.userSelectedSliderValue = false;
            let resultObj = result.filterValue;
            this.updateFilterValueSet(resultObj);
        });

        this.summaryStatisticsChange.emit(value);
    }

    onNuclideSingleSelectChange() {
        this.filterValues.summaryStatisticsMode = this.selectedSummaryStatisticsId;
        if (this.filterValues.nuclideId == null) {
            this.filterValues.nuclideId = `${this.nuclideOptions[0].id}`;
        } else {
            this.filterValues.nuclideId = `${this.singleSelectedNuclideId}`;
        }
        this.resetSamplingDepth();
        this.sendFilterValue();

        this.filterService.getFilterValues(this.filterValues).subscribe((result) => {
            this.userSelectedValueSet = null;
            this.userSelectedSliderValue = false;
            let resultObj = result.filterValue;
            this.updateFilterValueSet(resultObj);
        });
    }

    sendFilterValue() {
        this.filterChanged.emit(this.filterValues);
    }

    getNuclideIds(result: any) {
        if (this.selectedSummaryStatisticsId == 'uniqueSampleCount' || this.selectedSummaryStatisticsId == undefined || this.selectedSummaryStatisticsId == null) {
            return result;
        } else {
            return `${this.singleSelectedNuclideId}`;
        }
    }

    applyFilterAnalysisOption(filterValues: any, analysisOption: number, currentValueSet: any, keepNuclide: boolean = false) {
        const isMeasurementAnalysis = analysisOption == 2;
        const isSampleAnalysis = analysisOption == 1;
        const summaryStatisticsMode = isSampleAnalysis ? 'uniqueSampleCount' : 'meanActivity';

        if (isSampleAnalysis) {
            const selectedNuclides = this.getSelectedNuclideIds(currentValueSet);
            filterValues.nuclideId = selectedNuclides.join(',');
        }

        if (isMeasurementAnalysis) {
            const firstNuclideId = this.singleSelectedNuclideId;
            filterValues.nuclideId = firstNuclideId.toString();
        }

        filterValues.summaryStatisticsMode = summaryStatisticsMode;
    }

    private getSelectedNuclideIds(currentValueSet: any): number[] {
        return currentValueSet.nuclides.data.filter((x) => x.isSelected && !x.isDisabled).map((x) => x.id);
    }

    private cloneDefaultValueSet() {
        const defaultSet = JSON.parse(JSON.stringify(this.defaultValueSet));

        const pipe = new RemoveDuplicatesByIdPipe();
        defaultSet['nuclides'].data = pipe.transform(defaultSet['nuclides'].data);

        return defaultSet;
    }

    private resetAnalysisOption() {
        this.selectedAnalysisOptionId = 1;
    }

    private resetSummaryStatisticsMode() {
        this.selectedSummaryStatisticsId = 'uniqueSampleCount';
    }

    private resetNuclides(keepNuclide: boolean = false) {
        const pipe = new RemoveDuplicatesByIdPipe();

        // 2. reset the nuclides
        this.newSelectedValueSet['nuclides'] = this.cloneDefaultValueSet()['nuclides'];

        // 3. set the selected nuclides
        if (keepNuclide) {
            if (this.selectedAnalysisOptionId == 1) {
                if (this.memorizedSelectedNuclidesInSampleMode.length != 0) {
                    this.newSelectedValueSet['nuclides'].data.forEach((item) => {
                        if (this.memorizedSelectedNuclidesInSampleMode.includes(item.id) && !item.isDisabled && !item.isHidden) {
                            item.isSelected = true;
                        } else {
                            item.isSelected = false;
                        }
                    });
                }
                this.newSelectedValueSet['nuclides'].count = this.newSelectedValueSet['nuclides'].data.filter((x) => x.isSelected && !x.isDisabled).length;
            } else {
                // if the last filter action is analysis option change, then we need to reset the nuclides
                if (this._filterActionsHistory[this._filterActionsHistory.length - 1] == 'analysisOptionChanged') {
                    if (this.memorizedSelectedNuclidesInSampleMode.length) {
                        this.singleSelectedNuclideId = +this.memorizedSelectedNuclidesInSampleMode[0];
                    } else {
                        this.singleSelectedNuclideId = +this.newSelectedValueSet['nuclides'].data[0].id;
                    }
                }

                if (this._filterActionsHistory[this._filterActionsHistory.length - 1] == 'sampleTypeChanged') {
                    // try to find this.singleSelectedNuclideId in the new nuclides list. If it is not there, then reset it to the first one
                    const nuclides = this.newSelectedValueSet['nuclides'].data;
                    const nuclideIds = nuclides.map((x) => x.id);
                    const index = nuclideIds.indexOf(this.singleSelectedNuclideId);
                    if (index == -1) {
                        this.singleSelectedNuclideId = +nuclideIds[0];
                    }
                }
            }
        }
    }

    private resetSamplingYears() {
        this.newSelectedValueSet['samplingYears'] = this.cloneDefaultValueSet()['samplingYears'];
    }

    private resetDataSets() {
        this.newSelectedValueSet['dataSets'] = this.cloneDefaultValueSet()['dataSets'];
    }

    private resetAreas() {
        this.newSelectedValueSet['seaAreas'] = this.cloneDefaultValueSet()['seaAreas'];
    }

    private resetBodyParts() {
        const bodyParts = this.cloneDefaultValueSet()['bodyParts'];
        if (bodyParts) {
            this.newSelectedValueSet['bodyParts'] = bodyParts;
        } else {
            delete this.newSelectedValueSet['bodyParts'];
        }
    }

    private resetBioGroups() {
        const bioGroups = this.cloneDefaultValueSet()['bioGroups'];
        if (bioGroups) {
            this.newSelectedValueSet['bioGroups'] = bioGroups;
        } else {
            delete this.newSelectedValueSet['bioGroups'];
        }
    }

    private resetShapeType() {
        this.selectedShapeTypeId = 1;
    }

    private resetSamplingDepth() {
        this.pushFilterActionHistory('resetSamplingDepth');
        this.newSelectedValueSet['samplingDepth'] = this.cloneDefaultValueSet()['samplingDepth'];
        let samplingDepth = JSON.parse(this.marisData.masterData.ss11[0].filtervalue).samplingDepth[0];
        this.defaultValues.min = samplingDepth.min;
        this.defaultValues.max = samplingDepth.max;
        this.relevantValues.min = samplingDepth.min;
        this.relevantValues.max = samplingDepth.max;
        this.oldSelectionValues.min = samplingDepth.min;
        this.oldSelectionValues.max = samplingDepth.max;
        this.newSelectionValues.min = samplingDepth.min;
        this.newSelectionValues.max = samplingDepth.max;

        this.resetSlider = false;

        setTimeout(() => {
            this.resetSlider = true;
            this.userSelectedSliderValue = false;
        }, 100);
    }

    private resetPopup() {
        this.hidePopup();
        this.popupService.setPopupStatus('reset');
    }

    private resetFilterControls(filterOptions?: FilterOptions) {
        this.resetNuclides(filterOptions?.keepNuclide);
        this.resetSamplingYears();
        this.resetDataSets();
        this.resetAreas();
        this.resetSamplingDepth();
        this.resetBodyParts();
        this.resetBioGroups();

        if (!filterOptions?.skip?.shapeType) {
            this.resetShapeType();
        }
        if (!filterOptions?.skip?.analysisOption) {
            this.resetAnalysisOption();
            this.resetSummaryStatisticsMode();
        }
    }

    private initValueSets() {
        this.defaultValueSet = this.filterData.defaultFilterValues['ss' + this.selectedSampleTypeId + this.selectedShapeTypeId];
        this.oldSelectedValueSet = JSON.parse(JSON.stringify(this.defaultValueSet));
        this.newSelectedValueSet = JSON.parse(JSON.stringify(this.defaultValueSet));
    }

    private resetDefaultValueSet() {
        this.defaultValueSet = this.filterData.defaultFilterValues['ss' + this.selectedSampleTypeId + this.selectedShapeTypeId];
    }

    private initFilterData() {
        this.filterData = this.marisData.filterData;
        this.filters = this.filterData.configuration.all.map((data) => {
            return {
                id: data.id,
                value: data.value,
                tp: data.tp == 'dataSelectorTemplate' ? this.dataSelectorTemplate : this.sliderTemplate,
            };
        });
    }

    private pushFilterActionHistory(action: FilterActions) {
        this._filterActionsHistory.push(action);
    }
}

@Pipe({
    name: 'removeDuplicatesById',
})
export class RemoveDuplicatesByIdPipe implements PipeTransform {
    transform(value: any[]): any[] {
        const uniqueItems = new Map<number, any>();

        for (const item of value) {
            if (!uniqueItems.has(item.id)) {
                uniqueItems.set(item.id, item);
            }
        }

        return Array.from(uniqueItems.values());
    }
}

@Pipe({ name: 'noLeadingZero' })
export class NoLeadingZeroPipe implements PipeTransform {
    transform(value: string): string {
        if (value?.startsWith('0.')) {
            return value;
        }
        if (value === '00') {
            return '0';
        }
        const formattedValue = value?.replace(/^0+/, '');
        return formattedValue;
    }
}
